/**
 * Metrics API Data Fetching Hook
 *
 * Fetches metrics data from the Metrics API and transforms it to the expected format
 * for consumption by Dashboard charts and visualizations.
 */

import { useContext } from 'react';
import useSWR from 'swr';
import { MetricsQueryParams } from '../../utils/formatMetricsQuery';
import { GlobalContext } from '../contexts/GlobalContext';
import { DEFAULT_API_VERSION } from '../../client/Hookdeck';

interface MetricsAPIDataPoint {
  time_bucket: string | null;
  dimensions: Record<string, string | null>;
  metrics: Record<string, number>;
}

interface MetricsAPIResponse {
  data: MetricsAPIDataPoint[];
  metadata: {
    granularity: string;
    query_time_ms: number;
    row_count: number;
    row_limit: number;
    truncated: boolean;
  };
}

/**
 * Transform Metrics API response to the expected chart data format
 * Maps API field names to the format expected by Dashboard visualizations
 * Note: Keeps snake_case for dimensions and metrics as returned by the API
 */
function transformMetricsResponse(
  data: MetricsAPIDataPoint[],
  metrics_query: MetricsQueryParams,
): any[] {
  const { model, measure, time_dimension, granularity } = metrics_query;

  return data.map((row) => {
    const result: Record<string, any> = {};

    // Map time_bucket to time dimension format
    if (row.time_bucket) {
      // Base time dimension (e.g., "Events.createdAt")
      result[`${model}.${time_dimension}`] = row.time_bucket;

      // Granular time dimension (e.g., "Events.createdAt.minute")
      if (granularity) {
        result[`${model}.${time_dimension}.${granularity}`] = row.time_bucket;
      }
    }

    // Map metrics to measure format (e.g., "Events.count", "Requests.accepted_count")
    Object.entries(row.metrics).forEach(([metric_key, metric_value]) => {
      result[`${model}.${metric_key}`] = String(metric_value);
    });

    // Map dimensions to dimension format (e.g., "Events.source_id", "Events.webhook_id")
    Object.entries(row.dimensions).forEach(([dim_key, dim_value]) => {
      result[`${model}.${dim_key}`] = dim_value;
    });

    return result;
  });
}

/**
 * Hook to fetch and transform Metrics API data
 */
export const useMetricsQuery = <T>(
  metrics_query: MetricsQueryParams | null | undefined,
  options: {
    resetResultSetOnChange?: boolean;
    refreshKey?: number;
    revalidateOnFocus?: boolean;
  } = {},
): { is_loading: boolean; raw_data: T[] | null; refetch: () => void; error: Error | null } => {
  const { configs, HookdeckAPI } = useContext(GlobalContext);

  // Build the API URL
  const api_url = (() => {
    if (!metrics_query) return null;

    const { endpoint, query_params } = metrics_query;
    const params = new URLSearchParams();
    const null_filter_params: string[] = [];

    // Add date range
    params.append('date_range[start]', query_params.date_range.start);
    params.append('date_range[end]', query_params.date_range.end);

    // Add granularity
    if (query_params.granularity) {
      params.append('granularity', query_params.granularity);
    }

    // Add measures
    query_params.measures.forEach((measure) => {
      params.append('measures[]', measure);
    });

    // Add dimensions
    if (query_params.dimensions && query_params.dimensions.length > 0) {
      query_params.dimensions.forEach((dim) => {
        params.append('dimensions[]', dim);
      });
    }

    // Add filters
    if (query_params.filters) {
      Object.entries(query_params.filters).forEach(([key, value]) => {
        // A bare query-string key is parsed as null by the API and is distinct
        // from an omitted filter (for example, an unknown delivery group).
        if (value === null) {
          null_filter_params.push(`filters%5B${encodeURIComponent(key)}%5D`);
          return;
        }

        // Skip undefined or empty string values
        if (value === undefined || value === '') return;

        if (Array.isArray(value)) {
          value.forEach((v) => {
            if (v === null) {
              null_filter_params.push(`filters%5B${encodeURIComponent(key)}%5D%5B%5D`);
            } else if (v !== undefined && v !== '') {
              params.append(`filters[${key}][]`, String(v));
            }
          });
        } else if (typeof value === 'object' && value !== null) {
          // Handle nested objects (e.g., { min: 1, max: 5 })
          Object.entries(value).forEach(([nested_key, nested_value]) => {
            if (nested_value !== undefined && nested_value !== null && nested_value !== '') {
              params.append(`filters[${key}][${nested_key}]`, String(nested_value));
            }
          });
        } else {
          params.append(`filters[${key}]`, String(value));
        }
      });
    }

    const query_string = [params.toString(), ...null_filter_params].filter(Boolean).join('&');
    return `${configs.API_URL}/${DEFAULT_API_VERSION}/metrics/${endpoint}?${query_string}`;
  })();

  const {
    data: api_response,
    mutate,
    error,
    isLoading,
  } = useSWR<MetricsAPIResponse>(
    api_url ? `metrics-${api_url}-${HookdeckAPI.team_id}-${JSON.stringify(options)}` : null,
    async () => {
      if (!api_url)
        return {
          data: [],
          metadata: {
            granularity: '',
            query_time_ms: 0,
            row_count: 0,
            row_limit: 0,
            truncated: false,
          },
        };

      // Add x-hookdeck-team-id header for session authentication
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (HookdeckAPI.team_id) {
        headers['x-hookdeck-team-id'] = HookdeckAPI.team_id;
      }

      const response = await fetch(api_url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        // Extract detailed error information from the API response
        let error_details: any;
        try {
          error_details = await response.json();
        } catch {
          // Fallback if response body can't be parsed
          error_details = { message: response.statusText };
        }

        // Build a comprehensive error message
        const error_message = error_details.message || error_details.error || response.statusText;
        const error = new Error(`Metrics API Error [${response.status}]: ${error_message}`);

        // Attach structured context for debugging and error reporting tools (e.g., Sentry)
        (error as any).context = {
          status: response.status,
          status_text: response.statusText,
          url: api_url,
          team_id: HookdeckAPI.team_id,
          endpoint: metrics_query?.endpoint,
          error_code: error_details.error,
          error_data: error_details.data,
          query_params: metrics_query?.query_params,
        };

        throw error;
      }

      return response.json();
    },
    {
      ...(options.revalidateOnFocus !== undefined && {
        revalidateOnFocus: options.revalidateOnFocus,
      }),
      shouldRetryOnError: (error) => (error as any).context?.status !== 429,
    },
  );

  const raw_data = (() => {
    if (!api_response || !metrics_query) {
      return null;
    }

    // Extract the data array from the response
    const data_points = api_response.data || [];

    // Transform API response to expected format
    const transformed = transformMetricsResponse(data_points, metrics_query);

    // Normalize the data
    return transformed.map((row: Record<string, any>) => {
      const cloned_row = { ...row };

      // Replace empty strings with null for consistency
      Object.keys(cloned_row).forEach((key) => {
        if (cloned_row[key] === '') {
          cloned_row[key] = null;
        }
      });

      return cloned_row;
    }) as T[];
  })();

  return {
    is_loading: isLoading,
    raw_data,
    refetch: mutate,
    error: error,
  };
};

export default useMetricsQuery;
